import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-ad9118ca"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "kam-form-edit"
}
const _hoisted_2 = { class: "page-title-wrapper" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("b", null, "Weet je zeker dat je het formulier definitief wilt afronden?", -1))
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "no-mb" }, "Aanpassingen zijn hierna niet meer mogelijk.", -1))
const _hoisted_5 = {
  key: 1,
  class: "kam-form-error"
}
const _hoisted_6 = { key: 2 }
const _hoisted_7 = { class: "kam-form-skeleton" }
const _hoisted_8 = { class: "skeleton-actions" }

import PageTitleBar from '@/components/PageTitleBar.vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { computed, h, onMounted, ref, watch } from 'vue'
import axios from 'axios'
import { getKamFormById, getKamFormByReferenceLatest, getKamFormByReference } from '@/views/kam/formResigstry'
import type { KamFormRegistryEntry } from '@/views/kam/formResigstry'
import kamStore from '@/stores/kam.ts'
import type { KAMFormLike } from '@/stores/kam.ts'
import { Button, notification } from 'ant-design-vue'
import { KAMFormStatuses } from '@/helpers/kamFormStates'
import { openUrl } from '@/helpers/globalFunctions'
import { ensureAnswersMedia } from '@/helpers/kamFormUtils'
import { authStore } from '@/stores/auth'

type PersonOption = { id: number; full_name: string }


export default /*#__PURE__*/_defineComponent({
  __name: 'KAMFormEdit',
  setup(__props) {

function normalizePeople (people: Array<{ id: number | string; name?: string; full_name?: string }> | undefined | null): PersonOption[] {
  if (!people) return []
  return people
    .filter(p => p && p.id != null)
    .map(p => ({ id: Number(p.id), full_name: p.full_name ?? p.name ?? '' }))
    .filter(p => !Number.isNaN(p.id) && p.full_name.trim().length > 0)
    .sort((a, b) => a.full_name.localeCompare(b.full_name, 'nl', { sensitivity: 'base' }))
}

const projectLeaders = computed<PersonOption[]>(() => {
  const filters = kamStore().kamForm?.filters as { project_leaders?: Array<{ id: number | string; name?: string; full_name?: string }> } | undefined
  return normalizePeople(filters?.project_leaders)
})

// Add mapping for site_managers -> siteManagers prop (include phone + full_name)
const siteManagers = computed<PersonOption[] & { phone?: string }[]>(() => {
  const filters = kamStore().kamForm?.filters as { site_managers?: Array<{ id: number | string; name?: string; full_name?: string; phone?: string }> } | undefined
  const people = filters?.site_managers ?? []
  return people
    .filter(p => p && p.id != null)
    .map(p => ({ id: Number(p.id), full_name: p.full_name ?? p.name ?? '', phone: p.phone ?? '' }))
    .filter(p => !Number.isNaN(p.id) && p.full_name.trim().length > 0)
    .sort((a, b) => a.full_name.localeCompare(b.full_name, 'nl', { sensitivity: 'base' }))
})

// Add mapping for employees -> constructionWorkers prop
const constructionWorkers = computed<PersonOption[]>(() => {
  const filters = kamStore().kamForm?.filters as { workers?: Array<{ id: number | string; name?: string; full_name?: string }> } | undefined
  return normalizePeople(filters?.workers)
})

// Employees: use filters.employees when provided (separate from workers)
const employees = computed<PersonOption[]>(() => {
  const filters = kamStore().kamForm?.filters as { employees?: Array<{ id: number | string; name?: string; full_name?: string }> } | undefined
  return normalizePeople(filters?.employees)
})

const qualityManagement = computed<PersonOption[]>(() => {
  const filters = kamStore().kamForm?.filters as { quality_management?: Array<{ id: number | string; name?: string; full_name?: string }> } | undefined
  return normalizePeople(filters?.quality_management)
})

const route = useRoute()
const router = useRouter()

const formId = ref<number|null>(null)
const formReference = ref<string|null>(null)
const dirtyNotificationKey = 'kam-form-dirty-leave'
const pendingDirtyLeavePath = ref<string|null>(null)
const dirtyNavigationAllowed = ref(false)
const dirtyPromptActionInProgress = ref(false)

const loading = ref(false)
const saving = ref(false)
const previewingPdf = ref(false)
const isDirty = ref(false)
const loadFailed = ref(false)
const isQualityManagement = computed(() => {
  const roleNames = authStore().user?.roleNames ?? []
  return roleNames.includes('quality_management') || roleNames.includes('developer')
})
const editable = computed(() => {
  const f = kamStore().kamForm
  if (!f) return false

  if (f.status === KAMFormStatuses.Concept) return true
  if (f.status === KAMFormStatuses.Reported) return isQualityManagement.value

  return false
})
const isKAM004Form = computed(() => {
  return kamStore().kamForm?.form?.reference === 'KAM004' || formReference.value === 'KAM004'
})
const showFinalizeButton = computed(() => {
  const form = kamStore().kamForm
  if (!form || form.status === KAMFormStatuses.Closed) return false
  if (isKAM004Form.value) return false
  return editable.value && form.status === KAMFormStatuses.Reported
})
const canPreviewPdf = computed(() => {
  const form = kamStore().kamForm
  return !!form && form.status !== KAMFormStatuses.Concept
})
const allRequiredFilled = ref<boolean>(true)
const saveAllowed = ref<boolean>(true)

onMounted(() => {
  getFormId()
  getKAMForm()
})

onBeforeRouteLeave((to, from, next) => {
  if (dirtyNavigationAllowed.value) {
    dirtyNavigationAllowed.value = false
    resetFields()
    next()
  } else if (isDirty.value) {
    showDirtyLeaveNotification(to.fullPath)
    next(false)
  } else {
    closeDirtyLeaveNotification()
    resetFields()
    next()
  }
})

function showDirtyLeaveNotification (targetPath: string): void {
  pendingDirtyLeavePath.value = targetPath
  dirtyPromptActionInProgress.value = false
  notification.warning({
    key: dirtyNotificationKey,
    message: 'Niet opgeslagen wijzigingen',
    description: 'Je hebt niet opgeslagen wijzigingen. Wil je deze opslaan voordat je verder gaat?',
    duration: 0,
    onClose: handleDirtyNotificationClose,
    btn: () => h('div', { style: { display: 'flex', gap: '8px', justifyContent: 'flex-end' } }, [
      h(Button, {
        size: 'small',
        onClick: () => discardChangesAndLeave()
      }, () => 'Niet opslaan'),
      h(Button, {
        type: 'primary',
        size: 'small',
        loading: saving.value,
        disabled: !saveAllowed.value,
        onClick: () => saveChangesAndLeave()
      }, () => 'Opslaan')
    ])
  })
}

function closeDirtyLeaveNotification (): void {
  dirtyPromptActionInProgress.value = true
  notification.close(dirtyNotificationKey)
}

function handleDirtyNotificationClose (): void {
  if (dirtyPromptActionInProgress.value) return
  discardChangesAndLeave()
}

async function saveChangesAndLeave (): Promise<void> {
  if (saving.value || !kamStore().kamForm) return

  const targetPath = pendingDirtyLeavePath.value
  if (!targetPath) return

  try {
    await update(kamStore().kamForm.status, true)
    closeDirtyLeaveNotification()
    dirtyNavigationAllowed.value = true
    resetFields()
    await router.push(targetPath)
  } catch {
    dirtyPromptActionInProgress.value = false
    // The store already shows the save error. Keep the user on the form.
  }
}

async function discardChangesAndLeave (): Promise<void> {
  const targetPath = pendingDirtyLeavePath.value
  if (!targetPath) return

  closeDirtyLeaveNotification()
  isDirty.value = false
  dirtyNavigationAllowed.value = true
  resetFields()
  await router.push(targetPath)
}

function resetFields () {
  formId.value = null
  formReference.value = null
  pendingDirtyLeavePath.value = null
  kamStore().kamForm = null
  saveAllowed.value = true
}

async function update (currentStatus: number, rethrow = false) : Promise<void> {
  if (!saveAllowed.value) {
    if (rethrow) throw new Error('Formulier kan niet worden opgeslagen zonder actiedatum.')
    return
  }
  saving.value = true
  try {
    await kamStore().saveForm(kamStore().kamForm.id, kamStore().kamForm.answers as Record<string, unknown>, currentStatus)
    notification.success({
      message: 'Opgeslagen',
      description: 'De wijzigingen zijn succesvol opgeslagen.'
    })
    isDirty.value = false
  } catch (error) {
    // notification is already handled by the store
    if (rethrow) throw error
  } finally {
    saving.value = false
  }
}

async function previewKAMForm (): Promise<void> {
  const form = kamStore().kamForm
  if (!form) return

  previewingPdf.value = true
  try {
    const response = await axios.get(`${process.env.VUE_APP_API_URL}/kamForms/${form.id}/export`, { responseType: 'arraybuffer' })
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank', 'noopener')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch {
    notification.error({
      message: 'Preview mislukt',
      description: 'De PDF-preview kon niet worden geopend. Probeer het formulier eerst op te slaan of download de PDF.',
      duration: 0
    })
  } finally {
    previewingPdf.value = false
  }
}

const selectedComponent = computed(() => {
  if (!formId.value) {
    return null
  }
  const entry = getKamFormById(formId.value)
  return entry?.component ?? null
})

const pageHeaderText = computed((): { title: string, subtitle: string } => {
  const form = kamStore().kamForm
  if (!form) return { title: '', subtitle: '' }
  const name = form.form?.name ?? ''
  const title = `${name} #${route.params.kamFormId}`
  const project = form.project
  const subtitle = project ? `${project.name} (${project.reference})` : ''
  return { title, subtitle }
})

const reportUrl = computed(() => {
  const reports = kamStore().kamForm?.answers?.media?.reports
  if (!reports) return ''
  if (Array.isArray(reports)) return reports[0]?.url ?? ''
  return reports.url ?? ''
})

async function getKAMForm () {
  loading.value = true
  loadFailed.value = false

  try {
    const data = await kamStore().loadKAMFormById(Number(route.params.kamFormId)) as KAMFormLike
    ensureAnswersMedia(data)
    const { answers } = data
    const project = (data.project ?? null) as {
      reference?: string
      name?: string
      city?: string
      project_leader?: { id?: number | string }
      site_manager?: { id?: number | string }
    } | null
    const serverForm = data.form

    if (project && (!answers.projectReference || !answers.projectName || !answers.projectCity)) {
      if (!answers.projectReference && project.reference) answers.projectReference = project.reference
      if (!answers.projectName && project.name) answers.projectName = project.name
      if (!answers.projectCity && project.city) answers.projectCity = project.city
    }

    if (!answers.projectManagerId && project?.project_leader?.id) {
      answers.projectManagerId = project.project_leader.id
    }

    if (serverForm?.reference === 'KAM004' && !answers.projectLeaderId && project?.project_leader?.id) {
      answers.projectLeaderId = project.project_leader.id
    }

    if (serverForm?.reference === 'KAM004' && !answers.siteManagerId && project?.site_manager?.id) {
      answers.siteManagerId = project.site_manager.id
    }

    let resolvedEntry: KamFormRegistryEntry | undefined

    if (serverForm) {
      const ref = serverForm.reference
      const rawVersion = serverForm.version
      const version = rawVersion != null ? parseInt(String(rawVersion), 10) : NaN
      if (serverForm.reference && (typeof serverForm.version === 'number')) {
        resolvedEntry = getKamFormByReference(serverForm.reference, serverForm.version)
      }

      if (!resolvedEntry && ref && !Number.isNaN(version)) {
        resolvedEntry = getKamFormByReference(ref, version)
      }

      if (!resolvedEntry && typeof serverForm.id === 'number') {
        resolvedEntry = getKamFormById(serverForm.id)
      }

      if (!resolvedEntry && serverForm.reference) {
        resolvedEntry = getKamFormByReferenceLatest(serverForm.reference)
      }
    }

    if (!resolvedEntry && typeof route.params.formReference === 'string') {
      resolvedEntry = getKamFormByReferenceLatest(route.params.formReference)
    }

    if (resolvedEntry) {
      formId.value = resolvedEntry.id
      formReference.value = resolvedEntry.reference
    }
  } catch (e) {
    loadFailed.value = true
  } finally {
    loading.value = false
  }
}

// Watch the route parameter so we handle client-side route change
watch(() => route.params.formReference, () => {
  getFormId()
})

function getFormId () : void {
  const reference = route.params.formReference as string|null
  if (!reference) {
    return
  }
  const entry = getKamFormByReferenceLatest(reference)
  if (entry) {
    formId.value = entry.id
    formReference.value = entry.reference
  }
}


return (_ctx: any,_cache: any) => {
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_popconfirm = _resolveComponent("a-popconfirm")!
  const _component_a_empty = _resolveComponent("a-empty")!
  const _component_a_skeleton = _resolveComponent("a-skeleton")!
  const _component_a_col = _resolveComponent("a-col")!
  const _component_a_row = _resolveComponent("a-row")!

  return (!_unref(kamStore)().loading.item && _unref(kamStore)().kamForm)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(PageTitleBar, null, {
            title: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(pageHeaderText).title), 1)
            ]),
            subtitle: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(pageHeaderText).subtitle), 1)
            ]),
            buttons: _withCtx(() => [
              (_unref(showFinalizeButton))
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    (allRequiredFilled.value && saveAllowed.value && !saving.value)
                      ? (_openBlock(), _createBlock(_component_a_popconfirm, {
                          key: 0,
                          placement: "bottomRight",
                          "ok-text": "Ja, definitief afronden",
                          onConfirm: _cache[0] || (_cache[0] = ($event: any) => (update(_unref(KAMFormStatuses).Closed)))
                        }, {
                          title: _withCtx(() => [
                            _hoisted_3,
                            _hoisted_4
                          ]),
                          default: _withCtx(() => [
                            _createVNode(_component_a_button, { type: "primary" }, {
                              default: _withCtx(() => [
                                _createTextVNode("Definitief afronden")
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }))
                      : (_openBlock(), _createBlock(_component_a_button, {
                          key: 1,
                          type: "primary",
                          disabled: !allRequiredFilled.value || !saveAllowed.value || saving.value
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode("Definitief afronden")
                          ]),
                          _: 1
                        }, 8, ["disabled"]))
                  ], 64))
                : _createCommentVNode("", true),
              (_unref(editable) && _unref(kamStore)().kamForm.status !== _unref(KAMFormStatuses).Closed)
                ? (_openBlock(), _createBlock(_component_a_button, {
                    key: 1,
                    type: "primary",
                    disabled: saving.value || !_unref(editable) || !saveAllowed.value,
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (update(_unref(kamStore)().kamForm.status)))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode("Opslaan")
                    ]),
                    _: 1
                  }, 8, ["disabled"]))
                : _createCommentVNode("", true),
              (_unref(canPreviewPdf))
                ? (_openBlock(), _createBlock(_component_a_button, {
                    key: 2,
                    loading: previewingPdf.value,
                    onClick: previewKAMForm
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode("Preview PDF")
                    ]),
                    _: 1
                  }, 8, ["loading"]))
                : _createCommentVNode("", true),
              (_unref(kamStore)().kamForm.status !== _unref(KAMFormStatuses).Concept && _unref(reportUrl))
                ? (_openBlock(), _createBlock(_component_a_button, {
                    key: 3,
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(openUrl)(_unref(reportUrl), '_blank')))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode("Download PDF")
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_a_button, {
                onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(router).push({ name: 'KAMForms' })))
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Terug naar overzicht")
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _createElementVNode("div", null, [
          (_unref(selectedComponent))
            ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(selectedComponent)), {
                key: 0,
                answers: _unref(kamStore)().kamForm.answers,
                editable: _unref(editable),
                "project-leaders": _unref(projectLeaders),
                "site-managers": _unref(siteManagers),
                "construction-workers": _unref(constructionWorkers),
                employees: _unref(employees),
                "quality-management": _unref(qualityManagement),
                media: _unref(kamStore)().kamForm.answers.media,
                onChanged: _cache[4] || (_cache[4] = ($event: any) => (update(_unref(kamStore)().kamForm.status))),
                onDirty: _cache[5] || (_cache[5] = (v) => isDirty.value = v),
                onRequiredFilled: _cache[6] || (_cache[6] = (v) => allRequiredFilled.value = v),
                onSaveAllowed: _cache[7] || (_cache[7] = (v) => saveAllowed.value = v)
              }, null, 40, ["answers", "editable", "project-leaders", "site-managers", "construction-workers", "employees", "quality-management", "media"]))
            : (_openBlock(), _createBlock(_component_a_empty, {
                key: 1,
                description: "Geen formulier geselecteerd"
              }))
        ])
      ]))
    : (loadFailed.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(_component_a_empty, { description: "Het KAM-formulier kon niet worden geladen." }, {
            default: _withCtx(() => [
              _createVNode(_component_a_button, {
                onClick: _cache[8] || (_cache[8] = ($event: any) => (_unref(router).push({ name: 'KAMForms' })))
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Terug naar overzicht")
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_a_skeleton, {
              active: true,
              title: { width: '40%' },
              paragraph: { rows: 0 }
            }),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_component_a_skeleton, {
                active: true,
                paragraph: { rows: 1, width: ['30%', '20%'] }
              })
            ]),
            _createVNode(_component_a_skeleton, {
              active: true,
              paragraph: { rows: 4 }
            }),
            _createVNode(_component_a_row, { gutter: 16 }, {
              default: _withCtx(() => [
                _createVNode(_component_a_col, {
                  xs: { span: 24 },
                  md: { span: 12 }
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_skeleton, {
                      active: true,
                      paragraph: { rows: 6 }
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_col, {
                  xs: { span: 24 },
                  md: { span: 12 }
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_skeleton, {
                      active: true,
                      paragraph: { rows: 6 }
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ]))
}
}

})